/**
 * GlobalSearchIsland — modal full-screen de busca de produtos.
 *
 * Substitui `openGlobalSearch()` V1 (delivery_app.html:7308) que abria
 * SweetAlert text input + re-renderizava o `#menu-container` inteiro
 * com resultados. UX V2: input fica fixo no topo, resultados filtram
 * em tempo real ao digitar. Click no resultado abre o wizard normal
 * (window.openProductDetail interceptado pelo bootstrap → simples/pizza/
 * combo conforme tipo).
 *
 * Aberto via evento `aliv:open-global-search` (HeaderIsland → onClick lupa).
 * Helper window.openGlobalSearch interceptado em index.tsx pra paridade.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MenuCategory, MenuProduct } from '../types';
import { useModalOpen } from '../hooks/useAnyModalOpen';
import { t, tr, useI18n } from '../i18n';

declare global {
  interface Window {
    // MENU_DATA vem do global.d.ts (union de Array delivery + Object admin).
    openProductDetail?: (id: number) => void;
  }
}

function normalize(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function fmtBR(v: number): string {
  return v.toFixed(2).replace('.', ',');
}

interface ResultRow {
  product: MenuProduct;
  categoryName: string;
}

export function GlobalSearchIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [visible, setVisible] = useState(false);
  const [term, setTerm] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onOpen() {
      setTerm('');
      setVisible(true);
      // foco no input após mount + animação
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    window.addEventListener('aliv:open-global-search', onOpen);
    return () => window.removeEventListener('aliv:open-global-search', onOpen);
  }, []);

  useEffect(() => {
    if (!visible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  useModalOpen(visible, close);

  function close() {
    setVisible(false);
  }

  function pick(productId: number) {
    close();
    setTimeout(() => {
      try { window.openProductDetail?.(productId); } catch { /* */ }
    }, 200);
  }

  const results = useMemo<ResultRow[]>(() => {
    const t = normalize(term.trim());
    if (!t || t.length < 2) return [];
    const out: ResultRow[] = [];
    const seen = new Set<number>();
    for (const cat of (window.MENU_DATA as MenuCategory[] | undefined) || []) {
      for (const p of cat.items || []) {
        if (!p || p.is_hidden) continue;
        if (seen.has(p.id)) continue;
        const matchName = normalize(p.name).includes(t);
        const matchDesc = p.description && normalize(p.description).includes(t);
        if (matchName || matchDesc) {
          out.push({ product: p, categoryName: cat.category_name });
          seen.add(p.id);
        }
      }
    }
    return out.slice(0, 50); // teto pra UI não explodir em term curto
  }, [term, visible]);

  if (!visible) return null;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex flex-col bg-white">
      {/* Header sticky com input + close */}
      <div className="shrink-0 px-4 pt-3 pb-3 border-b border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-2 max-w-[480px] mx-auto w-full">
          <button
            type="button"
            onClick={close}
            aria-label={t("Fechar busca")}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition shrink-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <i className="fas fa-arrow-left text-sm" />
          </button>
          <div className="flex-1 relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
            <input
              ref={inputRef}
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t("Buscar produto...")}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 placeholder:text-gray-400"
            />
            {term && (
              <button
                type="button"
                onClick={() => { setTerm(''); inputRef.current?.focus(); }}
                aria-label={t("Limpar")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 flex items-center justify-center transition"
              >
                <i className="fas fa-times text-xs" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resultados — scroll natural */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-[480px] mx-auto w-full">
          {term.trim().length < 2 ? (
            <div className="py-16 text-center text-gray-400">
              <i className="fas fa-search text-5xl mb-3 opacity-30" />
              <p className="text-sm font-medium">{t('Digite pelo menos 2 letras')}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <i className="fas fa-frown text-5xl mb-3 opacity-30" />
              <p className="text-sm font-medium">Nada encontrado pra "{term}"</p>
              <p className="text-xs mt-1">{t('Tente outro termo')}</p>
            </div>
          ) : (
            <div className="px-4 py-3 space-y-2">
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider px-1 mb-1">
                {results.length} resultado{results.length === 1 ? '' : 's'}
              </p>
              {results.map(({ product, categoryName }) => {
                const isSoldOut = product.is_active === false;
                const price = Number(product.price) || 0;
                const hasSizes = !!product.size_prices && Object.keys(product.size_prices).length > 0;
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => !isSoldOut && pick(product.id)}
                    disabled={isSoldOut}
                    className={[
                      'w-full bg-white p-3 rounded-xl shadow-sm border flex items-start gap-3 text-left transition',
                      isSoldOut
                        ? 'border-gray-200 opacity-60 cursor-not-allowed'
                        : 'border-gray-100 hover:border-red-200 active:scale-[0.99]',
                    ].join(' ')}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        width={64}
                        height={64}
                        className="w-16 h-16 rounded-lg object-cover shrink-0 bg-gray-100"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-300 shrink-0">
                        <i className="fas fa-camera opacity-40" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wide truncate">
                        {categoryName}
                      </p>
                      <h3 className="text-sm font-bold text-gray-900 leading-tight">
                        {tr(product.name)}
                      </h3>
                      {product.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                          {tr(product.description)}
                        </p>
                      )}
                      <div className="mt-1.5">
                        {isSoldOut ? (
                          <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                            {t('Esgotado')}
                          </span>
                        ) : price === 0 ? (
                          <span className="text-[10px] font-bold text-orange-600 uppercase">
                            {t('A escolher')}
                          </span>
                        ) : (
                          <span className="text-xs font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">
                            {hasSizes || product.is_pizza ? t('A partir de ') : ''}R$ {fmtBR(price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
